import { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Progress } from '../ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { apiFetch } from '../../lib/api';
import { notify } from '../../lib/notify';
import { formatApiErrorBody } from '../../lib/formatApiError';
import { usePermission } from '../../hooks/usePermission';
import type { BrightBlueCatalogContext } from './types';
import { useCatalogImportJob } from './CatalogImportJobContext';
import type { CatalogImportJobDto, CatalogImportJobResult } from './catalogImportJobTypes';

interface CatalogExportPreview {
  metadata: {
    rootOperatorId: string;
    noOfCategories?: number;
    noOfProducts?: number;
    noOfVenues?: number;
    noOfMachines?: number;
    generatedAt?: string;
  };
  operators: BrightBlueCatalogContext['operators'];
  defaultOperatorId: string;
}

export type CatalogImportResult = CatalogImportJobResult;

interface CatalogExportImportProps {
  onCatalogContextUpdated?: () => void | Promise<void>;
}

function isCatalogSyncResult(value: unknown): value is {
  categoriesSynced: number;
  productsSynced: number;
  productsSkipped: number;
} {
  if (!value || typeof value !== 'object') return false;
  const row = value as Record<string, unknown>;
  return (
    typeof row.categoriesSynced === 'number' &&
    typeof row.productsSynced === 'number' &&
    typeof row.productsSkipped === 'number'
  );
}

export function CatalogExportImport({ onCatalogContextUpdated }: CatalogExportImportProps) {
  const { job, trackJob, isActive } = useCatalogImportJob();
  const canWriteIntegrations = usePermission('integrations.write');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<CatalogExportPreview | null>(null);
  const [operatorId, setOperatorId] = useState<string>('');
  const [includeChildSites, setIncludeChildSites] = useState(true);
  const [catalogLean, setCatalogLean] = useState(true);
  const [catalogOnly, setCatalogOnly] = useState(false);
  const [dryRun, setDryRun] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [starting, setStarting] = useState(false);
  const [syncingCatalog, setSyncingCatalog] = useState(false);
  const previewRequestId = useRef(0);
  const lastCompletedJobId = useRef<string | null>(null);

  const resetPreview = useCallback(() => {
    setPreview(null);
    setOperatorId('');
  }, []);

  const loadPreview = useCallback(
    async (selectedFile: File) => {
      const requestId = ++previewRequestId.current;
      setLoadingPreview(true);
      try {
        const form = new FormData();
        form.append('file', selectedFile);

        const res = await apiFetch('/api/v1/settings/integrations/brightblue/import-export/preview', {
          method: 'POST',
          body: form,
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(formatApiErrorBody(data));
        }

        if (requestId !== previewRequestId.current) {
          return;
        }

        setPreview(data as CatalogExportPreview);
        setOperatorId(data.defaultOperatorId ?? data.metadata.rootOperatorId);
        await onCatalogContextUpdated?.();
      } catch (err) {
        if (requestId !== previewRequestId.current) {
          return;
        }
        resetPreview();
        notify.error(err instanceof Error ? err.message : 'Could not read export file');
      } finally {
        if (requestId === previewRequestId.current) {
          setLoadingPreview(false);
        }
      }
    },
    [onCatalogContextUpdated, resetPreview]
  );

  useEffect(() => {
    if (!file) {
      resetPreview();
      return;
    }
    void loadPreview(file);
  }, [file, loadPreview, resetPreview]);

  useEffect(() => {
    if (!job || job.status !== 'completed' || !job.result) return;
    if (lastCompletedJobId.current === job.jobId) return;
    lastCompletedJobId.current = job.jobId;
    void onCatalogContextUpdated?.();
  }, [job, onCatalogContextUpdated]);

  const handleFileChange = (next: File | null) => {
    setFile(next);
  };

  const handleImport = async () => {
    if (!file || !preview || !operatorId) {
      notify.error('Choose a valid export file and operator first');
      return;
    }
    if (isActive) {
      notify.error('A catalog import is already running');
      return;
    }

    setStarting(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('operatorId', operatorId);
      form.append('includeChildSites', String(includeChildSites));
      form.append('catalogLean', String(catalogLean));
      form.append('catalogOnly', String(catalogOnly));
      form.append('dryRun', String(dryRun));

      const res = await apiFetch('/api/v1/settings/integrations/brightblue/import-export', {
        method: 'POST',
        body: form,
      });

      const data = await res.json().catch(() => ({}));
      if (res.status === 409 && data.job) {
        trackJob(data.job as CatalogImportJobDto);
        notify.error(data.message ?? 'A catalog import is already running');
        return;
      }
      if (!res.ok) {
        throw new Error(formatApiErrorBody(data));
      }

      trackJob(data as CatalogImportJobDto);
      notify.info(
        dryRun
          ? 'Dry run started — progress stays visible if you leave this page'
          : 'Import started — progress stays visible if you leave this page'
      );
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'Import failed to start');
    } finally {
      setStarting(false);
    }
  };

  const syncCatalog = async () => {
    setSyncingCatalog(true);
    try {
      const res = await apiFetch('/api/v1/settings/integrations/brightblue/catalog-sync', {
        method: 'POST',
      });
      const data: unknown = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(formatApiErrorBody(data));
      }
      if (!isCatalogSyncResult(data)) {
        throw new Error('Cloud catalog sync returned an unexpected response');
      }
      const skipped =
        data.productsSkipped > 0 ? `, ${data.productsSkipped} skipped` : '';
      notify.success(
        'Catalog synced from Cloud',
        `${data.categoriesSynced} categories, ${data.productsSynced} products${skipped}`
      );
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'Catalog sync failed');
    } finally {
      setSyncingCatalog(false);
    }
  };

  const result = job?.status === 'completed' ? job.result : null;
  const showJobProgress = !!job && (job.status === 'queued' || job.status === 'running');

  return (
    <Card>
      <CardHeader>
        <CardTitle>Catalog Export Import</CardTitle>
        <CardDescription>
          Import a Bright.Blue Product Catalog Export (JSON) to sync categories, products, venues,
          machines, and slots into Bright.Express. Choosing a file loads operators automatically for
          the connect step below. Progress continues in the top banner if you navigate away.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {canWriteIntegrations && (
          <div className="flex flex-col gap-3 border-b pb-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-medium">Sync from Cloud</p>
              <p className="text-xs text-muted-foreground">
                Pull categories and products for the linked operator. Uses the Cloud URL and webhook secret.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => void syncCatalog()}
              disabled={syncingCatalog || isActive}
            >
              {syncingCatalog ? 'Syncing catalog…' : 'Sync catalog'}
            </Button>
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor="catalog-export-file">Export file (.json)</Label>
          <input
            id="catalog-export-file"
            type="file"
            accept=".json,application/json"
            className="block w-full text-sm text-muted-foreground file:mr-4 file:rounded-md file:border-0 file:bg-muted file:px-4 file:py-2 file:text-sm file:font-medium"
            onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
            disabled={isActive}
          />
          <p className="text-xs text-muted-foreground">
            Generate this in Bright.Blue under Reports → Product Catalog Export.
          </p>
        </div>

        {loadingPreview && !preview && file && (
          <p className="text-sm text-muted-foreground">Reading export file…</p>
        )}

        {showJobProgress && (
          <div className="rounded-md border p-4 space-y-2 bg-muted/30">
            <div className="flex items-center justify-between gap-3 text-sm">
              <p className="font-medium">Import progress</p>
              <p className="tabular-nums text-muted-foreground">{job.progress.percent}%</p>
            </div>
            <Progress value={job.progress.percent} />
            <p className="text-sm text-muted-foreground">{job.progress.label}</p>
            <p className="text-xs text-muted-foreground">
              You can leave this page — progress stays in the banner at the top.
            </p>
          </div>
        )}

        {preview && (
          <div className="rounded-md border p-4 space-y-4 bg-muted/30">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-muted-foreground">Categories</p>
                <p className="font-medium">{preview.metadata.noOfCategories ?? '—'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Products</p>
                <p className="font-medium">{preview.metadata.noOfProducts ?? '—'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Venues</p>
                <p className="font-medium">{preview.metadata.noOfVenues ?? '—'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Machines</p>
                <p className="font-medium">{preview.metadata.noOfMachines ?? '—'}</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Operator scope</Label>
              <Select value={operatorId} onValueChange={setOperatorId} disabled={isActive}>
                <SelectTrigger>
                  <SelectValue placeholder="Select operator" />
                </SelectTrigger>
                <SelectContent>
                  {preview.operators.map((op) => (
                    <SelectItem key={op.id} value={op.id}>
                      {op.name}
                      {op.parentVendorId ? ' (child)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="includeChildSites"
                  checked={includeChildSites}
                  onCheckedChange={(v) => setIncludeChildSites(!!v)}
                  disabled={isActive}
                />
                <Label htmlFor="includeChildSites" className="font-normal cursor-pointer">
                  Include child operators (venues &amp; machines under descendants)
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="catalogLean"
                  checked={catalogLean}
                  onCheckedChange={(v) => setCatalogLean(!!v)}
                  disabled={isActive}
                />
                <Label htmlFor="catalogLean" className="font-normal cursor-pointer">
                  Lean catalog (only products/categories for selected operators)
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="catalogOnly"
                  checked={catalogOnly}
                  onCheckedChange={(v) => setCatalogOnly(!!v)}
                  disabled={isActive}
                />
                <Label htmlFor="catalogOnly" className="font-normal cursor-pointer">
                  Catalog only (skip venues, machines, and slots)
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="dryRun"
                  checked={dryRun}
                  onCheckedChange={(v) => setDryRun(!!v)}
                  disabled={isActive}
                />
                <Label htmlFor="dryRun" className="font-normal cursor-pointer">
                  Dry run (validate counts only)
                </Label>
              </div>
            </div>

            <Button type="button" onClick={handleImport} disabled={starting || isActive}>
              {starting || isActive
                ? 'Importing…'
                : dryRun
                  ? 'Run dry import'
                  : 'Import into Bright.Express'}
            </Button>
          </div>
        )}

        {result && (
          <div className="rounded-md border p-4 space-y-2 text-sm">
            <p className="font-medium">Import result</p>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-1">
              <li>Categories: {result.categoriesImported}</li>
              <li>Products: {result.productsImported}</li>
              <li>Venues: {result.venuesImported}</li>
              <li>Warehouses created: {result.warehousesCreated}</li>
              <li>Machines: {result.machinesImported}</li>
              <li>Slots: {result.slotsImported}</li>
            </ul>
            {result.errors.length > 0 && (
              <div className="mt-2 text-destructive">
                <p className="font-medium">Warnings / errors ({result.errors.length})</p>
                <ul className="list-disc pl-5 max-h-32 overflow-y-auto">
                  {result.errors.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {job?.status === 'failed' && job.error && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
            {job.error}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default CatalogExportImport;
